import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  useDraggable,
  rectIntersection,
  pointerWithin,
  closestCorners,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// Sortable template segment component
function SortableSegment({ segment, index, onDelete, onEdit, fields }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: segment.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const getSegmentLabel = () => {
    if (segment.type === 'field') {
      // Use display name from fields config
      const fieldConfig = fields?.[segment.value];
      const displayName = fieldConfig?.displayName || segment.value;
      return `{${displayName}}`;
    }
    return segment.value;
  };

  const getSegmentColor = () => {
    return segment.type === 'field' ? 'primary' : 'default';
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 1,
        p: 1,
        backgroundColor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        userSelect: 'none',
      }}
    >
      <Box {...attributes} {...listeners} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', userSelect: 'none', touchAction: 'none' }}>
        <DragIndicatorIcon fontSize="small" />
      </Box>
      
      <Chip
        label={getSegmentLabel()}
        color={getSegmentColor()}
        size="small"
        sx={{ flex: 1, userSelect: 'none' }}
      />
      
      {segment.type === 'text' && (
        <IconButton size="small" onClick={() => onEdit(index)}>
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      
      <IconButton size="small" onClick={() => onDelete(index)}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// Draggable field chip for the available fields panel
function DraggableField({ fieldName, displayName, enabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({ 
    id: `field-${fieldName}`, 
    disabled: !enabled 
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : enabled ? 1 : 0.4,
    cursor: enabled ? 'grab' : 'not-allowed',
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Chip
        label={displayName || fieldName}
        color="primary"
        variant="outlined"
        size="medium"
        disabled={!enabled}
        sx={{ m: 0.5, userSelect: 'none' }}
      />
    </Box>
  );
}

// Droppable template area component
function DroppableTemplateArea({ children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'template-dropzone',
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        p: 2,
        minHeight: 200,
        backgroundColor: isOver ? 'action.hover' : 'background.default',
        border: '2px dashed',
        borderColor: isOver ? 'primary.main' : 'divider',
        borderRadius: 1,
        transition: 'all 0.2s ease',
      }}
    >
      {children}
    </Box>
  );
}

// Thin drop zone rendered between items to indicate insertion position
function InsertionDropZone({ index }) {
  const { setNodeRef, isOver } = useDroppable({ id: `insert-${index}` });

  return (
    <Box ref={setNodeRef} sx={{ position: 'relative', height: 12, my: 0.5 }}>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          height: isOver ? 3 : 1,
          backgroundColor: isOver ? 'primary.main' : 'divider',
          transition: 'all 120ms ease',
          borderRadius: 1,
        }}
      />
    </Box>
  );
}

// Generate unique ID for segments
let segmentIdCounter = 0;
const generateSegmentId = () => `segment-${Date.now()}-${segmentIdCounter++}`;

const LinkTemplateBuilder = ({ template, fields, onChange, language = 'nl' }) => {
  
  // Text translations
  const text = {
    nl: {
      title: 'Link Template Configuratie',
      availableFields: 'Beschikbare velden',
      dragFieldsInfo: 'Sleep velden naar de template builder',
      templateStructure: 'Template structuur',
      addText: 'Tekst toevoegen',
      dragFieldsHere: 'Sleep velden hierheen of voeg tekst toe om een template te bouwen',
      livePreview: 'Live preview (met voorbeelddata)',
      noTemplate: 'Geen template gedefinieerd',
      editText: 'Tekst bewerken',
      addTextTitle: 'Tekst toevoegen',
      textLabel: 'Tekst',
      textHelper: 'Voer de tekst in die in de template moet worden gebruikt (bijv. \'?utm_source=\', \'&utm_campaign=\', \'_\')',
      cancel: 'Annuleren',
      save: 'Opslaan',
      add: 'Toevoegen'
    },
    en: {
      title: 'Link Template Configuration',
      availableFields: 'Available fields',
      dragFieldsInfo: 'Drag fields to the template builder',
      templateStructure: 'Template structure',
      addText: 'Add text',
      dragFieldsHere: 'Drag fields here or add text to build a template',
      livePreview: 'Live preview (with sample data)',
      noTemplate: 'No template defined',
      editText: 'Edit text',
      addTextTitle: 'Add text',
      textLabel: 'Text',
      textHelper: 'Enter the text to be used in the template (e.g. \'?utm_source=\', \'&utm_campaign=\', \'_\')',
      cancel: 'Cancel',
      save: 'Save',
      add: 'Add'
    }
  };

  const t = text[language] || text.nl;
  const [activeId, setActiveId] = useState(null);
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [textDialogValue, setTextDialogValue] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [wasValidDrop, setWasValidDrop] = useState(false);

  // Ensure all segments have IDs - use useEffect to add IDs if missing
  React.useEffect(() => {
    const hasItemsWithoutId = template.some(segment => !segment.id);
    if (hasItemsWithoutId) {
      const templatesWithIds = template.map(segment => {
        if (!segment.id) {
          return { ...segment, id: generateSegmentId() };
        }
        return segment;
      });
      onChange(templatesWithIds);
    }
  }, [template, onChange]);

  // Use template directly since we ensure IDs exist via useEffect
  const templatesWithIds = template;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    setWasValidDrop(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    let isValidDrop = false;
    
    if (!over) {
      setWasValidDrop(false);
      setTimeout(() => setActiveId(null), 500);
      return;
    }

    const overId = over.id?.toString?.() || '';

    // Helper: insert at a specific index
    const insertAtIndex = (index, segment) => {
      const newTemplate = [...templatesWithIds];
      const safeIndex = Math.max(0, Math.min(index, newTemplate.length));
      newTemplate.splice(safeIndex, 0, segment);
      onChange(newTemplate);
    };

    // Dropping a new field from the palette
    if (active.id.toString().startsWith('field-')) {
      const fieldName = active.id.toString().replace('field-', '');
      const newSegment = { id: generateSegmentId(), type: 'field', value: fieldName };

      if (overId.startsWith('insert-')) {
        const index = parseInt(overId.replace('insert-', ''), 10);
        insertAtIndex(index, newSegment);
        isValidDrop = true;
      } else if (overId === 'template-dropzone') {
        // Fallback to end of list
        onChange([...templatesWithIds, newSegment]);
        isValidDrop = true;
      } else {
        // Over a segment: insert before that segment
        const overIndex = templatesWithIds.findIndex(s => s.id === overId);
        if (overIndex !== -1) {
          insertAtIndex(overIndex, newSegment);
          isValidDrop = true;
        }
      }
    } else {
      // Reordering an existing segment
      const oldIndex = templatesWithIds.findIndex(s => s.id === active.id);

      if (overId.startsWith('insert-')) {
        let index = parseInt(overId.replace('insert-', ''), 10);
        if (oldIndex !== -1) {
          const movingItem = templatesWithIds[oldIndex];
          const newTemplate = [...templatesWithIds];
          newTemplate.splice(oldIndex, 1);
          // Adjust index if item was before the insertion point
          if (oldIndex < index) index -= 1;
          newTemplate.splice(index, 0, movingItem);
          onChange(newTemplate);
          isValidDrop = true;
        }
      } else if (overId === 'template-dropzone') {
        if (oldIndex !== -1) {
          const newTemplate = [...templatesWithIds];
          const item = newTemplate.splice(oldIndex, 1)[0];
          newTemplate.push(item);
          onChange(newTemplate);
          isValidDrop = true;
        }
      } else {
        const overIndex = templatesWithIds.findIndex(s => s.id === overId);
        if (oldIndex !== -1 && overIndex !== -1 && oldIndex !== overIndex) {
          const newTemplate = arrayMove(templatesWithIds, oldIndex, overIndex);
          onChange(newTemplate);
          isValidDrop = true;
        }
      }
    }
    
    setWasValidDrop(isValidDrop);
    setTimeout(() => setActiveId(null), isValidDrop ? 0 : 500);
  };

  const handleDeleteSegment = (id) => {
    const newTemplate = templatesWithIds.filter((s) => s.id !== id);
    onChange(newTemplate);
  };

  const handleEditSegment = (id) => {
    const index = templatesWithIds.findIndex(s => s.id === id);
    if (index !== -1) {
      setEditingIndex(id);
      setTextDialogValue(templatesWithIds[index].value);
      setTextDialogOpen(true);
    }
  };

  const handleAddText = () => {
    setEditingIndex(null);
    setTextDialogValue('');
    setTextDialogOpen(true);
  };

  const handleTextDialogSave = () => {
    if (editingIndex !== null) {
      // Edit existing segment
      const newTemplate = templatesWithIds.map(s => 
        s.id === editingIndex 
          ? { ...s, value: textDialogValue }
          : s
      );
      onChange(newTemplate);
    } else {
      // Add new segment
      const newSegment = { id: generateSegmentId(), type: 'text', value: textDialogValue };
      onChange([...templatesWithIds, newSegment]);
    }
    setTextDialogOpen(false);
    setTextDialogValue('');
    setEditingIndex(null);
  };

  const handleTextDialogClose = () => {
    setTextDialogOpen(false);
    setTextDialogValue('');
    setEditingIndex(null);
  };

  // Generate preview
  const generatePreview = () => {
    const sampleData = {
      Bureau: 'dail',
      Medium: 'social',
      Titel: 'facebook',
      Doelgroep: 'demo',
      Uiting: 'video',
      Linknaam: 'test_uiting',
      Campagne: 'test_campagne',
      Datum: '01/15/2024',
      Url: 'https://example.com/page?param=value#section'
    };

    let result = '';
    let hasQueryParam = false;

    templatesWithIds.forEach((segment) => {
      if (segment.type === 'text') {
        result += segment.value;
        if (segment.value.includes('?')) hasQueryParam = true;
      } else if (segment.type === 'field') {
        const fieldValue = sampleData[segment.value] || '';
        
        // Handle special transforms
        if (segment.transform === 'baseUrl') {
          // Extract base URL (before # and ?)
          const url = fieldValue;
          const hashIndex = url.indexOf('#');
          const queryIndex = url.indexOf('?');
          
          let baseUrl = url;
          if (hashIndex !== -1 && queryIndex !== -1) {
            baseUrl = url.substring(0, Math.min(hashIndex, queryIndex));
          } else if (hashIndex !== -1) {
            baseUrl = url.substring(0, hashIndex);
          } else if (queryIndex !== -1) {
            baseUrl = url.substring(0, queryIndex);
            hasQueryParam = true;
          }
          result += baseUrl;
        } else if (segment.transform === 'fragment') {
          // Extract fragment (after #)
          const url = fieldValue;
          const hashIndex = url.indexOf('#');
          if (hashIndex !== -1) {
            result += url.substring(hashIndex);
          }
        } else if (segment.transform === 'removeSlashes') {
          // Remove slashes from date
          result += fieldValue.replace(/\//g, '');
        } else {
          result += fieldValue;
        }
      }
    });

    return result || t.noTemplate;
  };

  const fieldNames = Object.keys(fields);

  // Custom collision detection - use pointerWithin for more precise detection
  const customCollisionDetection = (args) => {
    // Use pointerWithin for accurate drop detection
    const pointerCollisions = pointerWithin(args);
    
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    
    // No collision
    return [];
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      measuring={{
        droppable: {
          strategy: 'always',
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column' }}>
        <Typography variant="h6" gutterBottom>
          {t.title}
        </Typography>

        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Available Fields Panel */}
          <Paper sx={{ p: 2, width: 250, flexShrink: 0 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t.availableFields}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {t.dragFieldsInfo}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {fieldNames.map((fieldName) => (
                <DraggableField
                  key={fieldName}
                  fieldName={fieldName}
                  displayName={fields[fieldName].displayName}
                  enabled={fields[fieldName].enabled}
                />
              ))}
            </Box>
          </Paper>

          {/* Template Builder */}
          <Paper sx={{ p: 2, flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">
                {t.templateStructure}
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddText}
                variant="outlined"
              >
                {t.addText}
              </Button>
            </Box>

            <SortableContext
              items={templatesWithIds.map(s => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <DroppableTemplateArea>
                {templatesWithIds.length === 0 ? (
                  <>
                    <InsertionDropZone index={0} />
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      {t.dragFieldsHere}
                    </Typography>
                  </>
                ) : (
                  templatesWithIds.map((segment, index) => (
                    <React.Fragment key={segment.id}>
                      <InsertionDropZone index={index} />
                      <SortableSegment
                        segment={segment}
                        index={index}
                        onDelete={() => handleDeleteSegment(segment.id)}
                        onEdit={() => handleEditSegment(segment.id)}
                        fields={fields}
                      />
                      {index === templatesWithIds.length - 1 && (
                        <InsertionDropZone index={index + 1} />
                      )}
                    </React.Fragment>
                  ))
                )}
              </DroppableTemplateArea>
            </SortableContext>
          </Paper>
        </Box>

        {/* Live Preview */}
        <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            {t.livePreview}
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'white',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              wordBreak: 'break-all',
              minHeight: 60,
            }}
          >
            {generatePreview()}
          </Box>
        </Paper>
      </Box>

      <DragOverlay 
        dropAnimation={
          wasValidDrop 
            ? { duration: 0, easing: 'ease' } // No animation for valid drops
            : { duration: 500, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' } // Bounce back for invalid drops
        }
      >
        {activeId ? (
          (() => {
            if (activeId.toString().startsWith('field-')) {
              // Dragging from available fields - show chip
              const fieldName = activeId.toString().replace('field-', '');
              return (
                <Chip
                  label={fields[fieldName]?.displayName || fieldName}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{ cursor: 'grabbing' }}
                />
              );
            } else {
              // Dragging an existing segment - show full segment box
              const segment = templatesWithIds.find(s => s.id === activeId);
              if (!segment) return null;
              
              const getSegmentLabel = () => {
                if (segment.type === 'field') {
                  const displayName = fields[segment.value]?.displayName || segment.value;
                  return `{${displayName}}`;
                }
                return segment.value;
              };
              
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    backgroundColor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'grabbing',
                    boxShadow: 3,
                  }}
                >
                  <DragIndicatorIcon fontSize="small" />
                  <Chip
                    label={getSegmentLabel()}
                    color={segment.type === 'field' ? 'primary' : 'default'}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Box>
              );
            }
            return null;
          })()
        ) : null}
      </DragOverlay>

      {/* Text Dialog */}
      <Dialog open={textDialogOpen} onClose={handleTextDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingIndex !== null ? t.editText : t.addTextTitle}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label={t.textLabel}
            value={textDialogValue}
            onChange={(e) => setTextDialogValue(e.target.value)}
            margin="normal"
            helperText={t.textHelper}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTextDialogClose}>{t.cancel}</Button>
          <Button onClick={handleTextDialogSave} variant="contained">
            {editingIndex !== null ? t.save : t.add}
          </Button>
        </DialogActions>
      </Dialog>
    </DndContext>
  );
};

export default LinkTemplateBuilder;

